// Harness colors
export const colors = {
  primary: {
    main: '#005755', //teal900
    light: '#28BEAF', //teal500
    dark: '#003c3d', //teal950
  },
  secondary: {
    main: '#2389C1',
    light: '#2DA1E2',
    dark: '#1E6D9A',
    states: {
      'focus-visible': 'rgba(35, 137, 193, 0.30)',
      selected: 'rgba(35, 137, 193, 0.08)',
    },
  },
  tertiary: {
    main: '#00646e',
  },
  error: {
    main: '#E94F74',
    light: '#FF5C83',
    dark: '#B43A57',
  },
  warning: {
    main: '#ED6C02',
    light: '#E65100',
    dark: '#FF9800',
  },
  success: {
    main: '#1DC068',
  },
  black: '#000000',
  white: '#ffffff',
  transparent: 'transparent',
  current: 'currentColor',
  green: {
    50: '#f5faf3',
    100: '#edf8e9',
    200: '#d1ebc7',
    300: '#aadb9a',
    400: '#7dc167',
    500: '#5aa641',
    600: '#468831',
    700: '#3a6b2a',
    800: '#305625',
    900: '#284720',
    950: '#12260d',
  },
  yellow: {
    50: '#fcf7ee',
    100: '#f7eace',
    200: '#eed399',
    300: '#e5b864',
    400: '#dea041',
    500: '#d6822a',
    600: '#b15d20',
    700: '#9d4820',
    800: '#803920',
    900: '#6a301d',
    950: '#3c170c',
  },
  purple: {
    50: '#faf5ff',
    100: '#f2e8ff',
    200: '#e7d4ff',
    300: '#d5b3ff',
    400: '#ba82fe',
    500: '#a052fa',
    600: '#8b30ed',
    700: '#761fd1',
    800: '#6920b1',
    900: '#531a89',
    950: '#370566',
  },
  red: {
    50: '#fef2f2',
    100: '#fde3e3',
    200: '#fccccc',
    300: '#f9a8a8',
    400: '#f47575',
    500: '#ea4949',
    600: '#d72b2b',
    700: '#b12020',
    800: '#951f1f',
    900: '#7c2020',
    950: '#430c0c',
  },
  teal: {
    50: '#ecfffd',
    100: '#bdfffd',
    200: '#7bfffc',
    300: '#31fffc',
    400: '#00fff2',
    500: '#00edda',
    600: '#00bfb5',
    700: '#009791',
    800: '#007774',
    900: '#005755',
    950: '#003c3d',
  },
  gray: {
    50: '#f6f7f7',
    100: '#e5ebe9',
    200: '#c0cfca',
    300: '#99afa8',
    400: '#748d87',
    500: '#59736d',
    600: '#465b56',
    700: '#3b4a47',
    800: '#323d3c',
    900: '#2c3533',
    950: '#161d1c',
  },
  grey: {
    50: '#FCFCFC',
    100: '#F7F9FA',
    200: '#F2F4F4',
    300: '#E1EDF1',
    400: '#C8D4DA',
    500: '#879499',
    600: '#545D63',
    700: '#343E45',
    800: '#2E404A',
    900: '#151616',
  },
  'deep-purple': {
    50: '#ede7f6',
    100: '#d1c4e9',
    200: '#b39ddb',
    300: '#9575cd',
    400: '#7e57c2',
    500: '#673ab7',
    600: '#5e35b1',
    700: '#512da8',
    800: '#4527a0',
    900: '#311b92',
  },
};

// Access Colors
// All exports below are theme-aware: they resolve via CSS vars defined in
// src/theme/index.css (`:root` + `.dark` blocks).
export const white = 'var(--white)';
/** @deprecated Prefer `var(--onSurface)` for body text. */
export const black = 'var(--onSurface)';
export const teal500 = 'var(--teal500)';
export const teal900 = 'var(--primary)';
export const orange500 = 'var(--orange500)';
export const red500 = 'var(--red500)';
/** @deprecated Use `var(--onSurfaceMuted)` for muted body text. */
export const darkGray = 'var(--onSurfaceMuted)';
/** @deprecated Use `var(--onSurfaceSubtle)`. */
export const disabledGray = 'var(--onSurfaceSubtle)';
/** @deprecated Use `var(--orange500)`. */
export const warningAmber = 'var(--orange500)';
/** @deprecated `darkBrown` was a misnomer; this is body text. Use `var(--onSurface)`. */
export const darkBrown = 'var(--onSurface)';
/** @deprecated `lightTan` was a misnomer; this is a muted surface. Use `var(--surfaceMuted)`. */
export const lightTan = 'var(--surfaceMuted)';
/** @deprecated Use `var(--surfaceSecondary)` for soft surface backgrounds. */
export const lightBlue = 'var(--surfaceSecondary)';
export const mediumBlue = 'var(--mediumBlue)';
export const darkBlue = 'var(--stateBlue)';
export const infoBannerBg = 'var(--infoBannerBg)';
export const infoBannerInk = 'var(--infoBannerInk)';
export const darkGreen = 'var(--darkGreen)';
/** @deprecated Use `var(--surfaceSecondary)` or `var(--surfaceMuted)`. */
export const sand = 'var(--surfaceSecondary)';
/** @deprecated Use `var(--mediumBlue)` or define a new semantic token. */
export const porpita = 'var(--mediumBlue)';
export const primary = 'var(--primary)';
export const primaryLight = 'var(--primaryLight)';
export const primaryDark = colors.primary.dark;
/** Dark-mode-aware brand color (matches the Tailwind `brand-primary` token). */
export const brandPrimary = 'var(--brandPrimary)';

// Off palette colors
/** @deprecated Use `var(--surfaceMuted)`. */
export const grey50 = 'var(--surfaceMuted)';
/** @deprecated Use `var(--surfaceMuted)`. */
export const grey100 = 'var(--surfaceMuted)';
/** @deprecated Use `var(--surfaceBorder)`. */
export const grey300 = 'var(--surfaceBorder)';

// SMB self-service signup design tokens — tailwind-derived greyscale +
// error/success/teal accents matching the partner-SMB visual spec. Kept
// separate from the canonical palette so they can be removed when the
// codebase adopts a unified tailwind-aligned ramp.
export const signupTokens = {
  borderNeutral: '#E5E7EB', // tailwind gray-200
  borderMid: '#D1D5DB', // tailwind gray-300
  surfaceMuted: '#F9FAFB', // tailwind gray-50
  surfaceDisabled: '#F3F4F6', // tailwind gray-100
  textDisabled: '#9CA3AF', // tailwind gray-400
  textBody: '#6B7280', // tailwind gray-500
  textBodyDark: '#4B5563', // tailwind gray-600
  textHeading: '#111827', // tailwind gray-900
  errorText: '#DC2626', // tailwind red-600
  // Links inside the error panel: red-600 only reaches 4.41:1 on the panel's
  // red-50 background, just under AA for body text. This clears it at 6.2:1.
  errorLink: '#B12020', // red-700
  primaryHoverDark: '#004544', // teal hover darker (brand)
  successSurface: '#D1FAE5', // tailwind green-100
  bannerSurface: '#F0FDFA', // tailwind teal-50
  bannerBorder: '#99F6E4', // tailwind teal-200
  bannerText: '#0F766E', // tailwind teal-700
} as const;

export const skype = '#00AFF0'; // brand color (Skype) — not theme-aware
export const linkedIn = '#0077B5'; // brand color (LinkedIn) — not theme-aware
export const borderColor = 'var(--borderColor)';
export const borderColorHex = 'var(--borderColorHex)';
/** @deprecated Use `var(--surfaceElevated)`. */
export const highlightedExpert = 'var(--surfaceElevated)';
/** @deprecated Use `var(--onSurfaceMuted)`. */
export const aboutEmployeeRole = 'var(--onSurfaceMuted)';
/** @deprecated Use `var(--onSurfaceSecondary)`. */
export const emailContentTitle = 'var(--onSurfaceSecondary)';
/** @deprecated Use `var(--surfaceHoverOverlay)`. */
export const whiteTransparent = 'var(--surfaceHoverOverlay)';
export const blue = 'var(--blue)';

// Dashboard semantic colors — aliases over the canonical on-surface tokens.
/** @deprecated Use `var(--onSurface)`. */
export const textPrimary = 'var(--onSurface)';
/** @deprecated Use `var(--onSurfaceSecondary)`. */
export const textSecondary = 'var(--onSurfaceSecondary)';
/** @deprecated Use `var(--onSurfaceMuted)`. */
export const textTertiary = 'var(--onSurfaceMuted)';
/** @deprecated Use `var(--onSurfaceSubtle)`. */
export const textMuted = 'var(--onSurfaceSubtle)';

// Raw dark-theme hex values.
// MUI's createPalette → augmentColor → decomposeColor cannot parse `var(--*)`,
// so the dark MUI palette in src/theme/index.ts pulls hex values from here.
// Keep these in sync with the `.dark` block of src/theme/index.css.
export const darkSurfaceBase = '#111111';
export const darkSurfaceCard = '#1A1A1A';
export const darkSurfaceElevated = '#242424';
export const darkSurfaceSecondary = '#2E2E2E';
export const darkSurfaceMuted = '#383838';
export const darkSurfaceBorder = 'rgba(255, 255, 255, 0.12)';
export const darkOnSurface = '#FFFFFF';
export const darkOnSurfaceSecondary = '#B0B8C1';
export const darkOnSurfaceMuted = '#9CA3AF';
export const darkOnSurfaceSubtle = '#6B7280';

// Semantic surface tokens (CSS-variable-backed for light/dark switching)
export const surfaceBase = 'var(--surfaceBase)';
export const surfaceCard = 'var(--surfaceCard)';
export const surfaceElevated = 'var(--surfaceElevated)';
export const surfaceSecondary = 'var(--surfaceSecondary)';
export const surfaceMuted = 'var(--surfaceMuted)';
export const surfaceBorder = 'var(--surfaceBorder)';

export default {
  primary: colors.primary.main,
  primaryLight: colors.primary.light,
  primaryDark: colors.primary.dark,
  white,
  black,
  teal500,
  teal900,
  orange500,
  red500,
  darkGray,
  darkBrown,
  lightTan,
  lightBlue,
  mediumBlue,
  darkBlue,
  darkGreen,
  sand,
  porpita,
  grey300,
  skype,
  linkedIn,
  borderColor,
  borderColorHex,
  highlightedExpert,
  aboutEmployeeRole,
  emailContentTitle,
  whiteTransparent,
  textPrimary,
  textSecondary,
  textTertiary,
  textMuted,
};
